<script lang="ts">  import { m } from '$lib/paraglide/messages';

  import { createEventDispatcher } from 'svelte';
  import Header from '$lib/ui/free-trial/header.svelte';
  import { translation } from '$lib/locale/locale';
  import Button from '$lib/ui/shared/buttons/button.svelte';
  import { api, wpApi } from '$lib/api';
  import type { User } from '$lib/models/user';
  import { userContext } from '$lib/stores/stores';
  import Spinner from '$lib/ui/shared/spinner.svelte';
  import googleLogo from '$lib/assets/shared/google_logo.svg';
  import Separator from '$lib/ui/shared/separator.svelte';
  import { click } from '$lib/tracking/tool';
  import { openGoogleAuth } from '$lib/utils/auth';
  import Alert from './alert.svelte';
  import { goto } from '$app/navigation';
  import { validateEmail } from '$lib/utils/validate';

  const dispatch = createEventDispatcher<{ login: User }>();
  const forgotPasswordDispatch = createEventDispatcher();
  const {} = translation();

  export let showSignUp = false;
  export let redirect = '';
  export let showHeader = true;
  export let email = '';
  export let disableEmailInput = false;

  let user = userContext.get();
  let password = '';
  let submitted = false;
  let loading = false;

  let error: string;

  function handleSignup() {
    click('signup');
    error = '';
    submitted = true;
    if (validateSignUpForm()) {
      loading = true;
      api<User>(fetch, `/v2/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: email,
          email: email,
          password: password,
          repeatPassword: password,
        }),
      })
        .then((user) => {
          autoLogin(user);
          dispatch('login', user);
          loading = false;
          if (redirect && redirect !== window.location.pathname) {
            goto(redirect);
          }
        })
        .catch((err) => {
          loading = false;
          let errorBody = JSON.parse(err.extra.body);
          error = errorBody.message ?? errorBody.error;
        });
    } else {
      loading = false;
    }
  }

  function handleLogin() {
    click('login');
    error = '';
    submitted = true;
    if (validateLoginForm()) {
      loading = true;
      api<User>(fetch, `/v2/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: email,
          password: password,
          rememberMe: true,
        }),
      })
        .then((user) => {
          autoLogin(user);
          dispatch('login', user);
          console.log('redirect', redirect);
          loading = false;
          if (redirect && redirect !== window.location.pathname) {
            console.log('redirecting to', redirect);
            goto(redirect);
          }
        })
        .catch((err) => {
          loading = false;
          let errorBody = JSON.parse(err.extra.body);
          error = errorBody.message ?? errorBody.error;
        });
    } else {
      loading = false;
    }
  }

  function handleForgotPassword() {
    click('forgot-password');
    forgotPasswordDispatch('forgot-password');
  }

  function validateSignUpForm() {
    return email && password && validateEmail(email);
  }

  function validateLoginForm() {
    return email && password;
  }

  function switchForm() {
    showSignUp = !showSignUp;
    password = '';
    error = '';
    submitted = false;
    // In the receipt email should not be reset when switching forms in order to prevent user from whitelisting with the wrong email
    if (!disableEmailInput) {
      email = '';
    }
  }

  function autoLogin(newUser: User) {
    user.set(newUser);
  }
</script>

<section>
  {#if showSignUp}
    {#if showHeader}
      <Header showBack={false} title={m['free-trial.create-account']()} subtitle={m['free-trial.enjoy-music']()} />
    {/if}
    <Button
      text={m['auth.google-sign-up']()}
      marginTop={'8px'}
      paddingBottom={'12px'}
      paddingTop={'12px'}
      paddingLeft={'19px'}
      paddingRight={'19px'}
      imageHeight={'24px'}
      imageWidth={'24px'}
      imageMarginRight={'10px'}
      fontSize={'var(--base-font-size)'}
      background={'white'}
      border={'1px solid var(--border-100)'}
      color={'var(--charleston-grey)'}
      leftImage={googleLogo}
      isSubmitButton={false}
      onClick={() => openGoogleAuth(user)}
      disabled={loading}
    />
    <Separator
      text={m['common.or']()}
      marginBottom={'20px'}
      backgroundColor={'white'}
      color={'var(--text-secondary)'}
      separatorColor={'#EAEBED'}
    />
    {#if error}
      <Alert description={error} marginBottom={'16px'} />
    {/if}
    <form on:submit|preventDefault={handleSignup}>
      <label>
        <div class="form-label">{m['common.email-address']()}</div>
        <input
          class="form-input"
          class:error={submitted && !email}
          type="email"
          bind:value={email}
          required
          autocomplete="email"
          disabled={loading || disableEmailInput}
        />

        {#if submitted && !email}
          <div class="form-error">{m['form-errors.required']()}</div>
        {:else if submitted && !validateEmail(email)}
          <div class="form-error">{m['form-errors.email']()}</div>
        {/if}
      </label>
      <label>
        <div class="form-label">{m['signup.password-placeholder']()}</div>
        <input
          class="form-input"
          class:error={submitted && !password}
          type="password"
          bind:value={password}
          required
          autocomplete="new-password"
          id="new-password"
          minlength="8"
          maxlength="128"
          disabled={loading}
        />

        <div class="form-hint">* {m['signup.password-requirement']()}</div>

        {#if submitted && !password}
          <div class="form-error">{m['form-errors.required']()}</div>
        {/if}
      </label>
      <!-- <button type="submit" disabled={!validateForm()}>Submit</button> -->
      {#if loading}
        <div class="d-flex flex-row justify-content-center spinner">
          <Spinner slim={true} size={'48px'} />
        </div>
      {:else}
        <Button
          text={m['cart.sign-up']()}
          paddingBottom={'12px'}
          paddingTop={'12px'}
          paddingLeft={'19px'}
          paddingRight={'19px'}
          marginTop={'24px'}
          imageHeight={'16px'}
          imageWidth={'16x'}
          fontSize={'var(--base-font-size)'}
          isSubmitButton={true}
        />
      {/if}
    </form>

    <div class="switch-auth-modal">
      <div>
        {m['cart.already-have-account']()}
      </div>
      <button class="link" on:click={switchForm} disabled={loading}>
        {m['login.login-button']()}
      </button>
    </div>
  {:else}
    {#if showHeader}
      <Header showBack={false} title={m['login.title']()} subtitle={m['login.sub-title']()} />
    {/if}
    <Button
      text={m['auth.google-log-in']()}
      marginTop={'8px'}
      paddingBottom={'12px'}
      paddingTop={'12px'}
      paddingLeft={'19px'}
      paddingRight={'19px'}
      imageHeight={'24px'}
      imageWidth={'24x'}
      imageMarginRight={'10px'}
      fontSize={'var(--base-font-size)'}
      background={'white'}
      border={'1px solid var(--border-100)'}
      color={'var(--charleston-grey)'}
      leftImage={googleLogo}
      isSubmitButton={false}
      onClick={() => openGoogleAuth(user)}
      disabled={loading}
    />
    <Separator
      text={m['common.or']()}
      marginBottom={'20px'}
      backgroundColor={'white'}
      color={'var(--text-secondary)'}
      separatorColor={'#EAEBED'}
    />
    {#if error}
      <Alert description={error} marginBottom={'16px'} />
    {/if}
    <form on:submit|preventDefault={handleLogin}>
      <label>
        <div class="form-label">{m['common.email-address']()}</div>
        <input
          class="form-input"
          class:error={submitted && !email}
          type="text"
          bind:value={email}
          required
          autocomplete="email"
          disabled={loading || disableEmailInput}
        />
        {#if submitted && !email}
          <div class="form-error">{m['form-errors.required']()}</div>
        {/if}
      </label>
      <label>
        <div class="form-label">{m['signup.password-placeholder']()}</div>
        <input
          class="form-input"
          class:error={submitted && !password}
          type="password"
          bind:value={password}
          required
          autocomplete="current-password"
          id="current-password"
          minlength="3"
          maxlength="128"
          disabled={loading}
        />
        {#if submitted && !password}
          <div class="form-error">{m['form-errors.required']()}</div>
        {/if}
      </label>
      <!-- Forgot password button -->
      <button type="button" class="forgot-password" on:click={handleForgotPassword} disabled={loading}>
        {m['login.forgot-password']()}
      </button>
      {#if loading}
        <div class="d-flex flex-row justify-content-center spinner">
          <Spinner slim={true} size={'48px'} />
        </div>
      {:else}
        <Button
          text={m['login.login-button']()}
          paddingBottom={'12px'}
          paddingTop={'12px'}
          paddingLeft={'19px'}
          paddingRight={'19px'}
          marginTop={'24px'}
          imageHeight={'16px'}
          imageWidth={'16x'}
          fontSize={'var(--base-font-size)'}
          isSubmitButton={true}
        />
      {/if}
    </form>
    <div class="switch-auth-modal">
      <div>
        {m['cart.new-to-hs']()}
      </div>
      <button class="link" on:click={switchForm} disabled={loading}>
        {m['cart.sign-up']()}
      </button>
    </div>
  {/if}
</section>

<style>
  section {
    display: flex;
    flex-direction: column;
  }

  .switch-auth-modal {
    display: flex;
    flex-direction: row;
    margin-top: 16px;
    color: var(--text-secondary);
    font-size: var(--xs-font-size);
  }
  .link {
    color: var(--magic-potion);
    margin-left: 3px;
  }

  form {
    display: flex;
    flex-direction: column;
  }
  .form-hint {
    font-size: 12px;
    color: var(--text-secondary);
    margin-top: 4px;
  }
  .forgot-password {
    color: var(--magic-potion);
    font-size: 14px;
    margin-top: 8px;
    width: fit-content;
    cursor: pointer;
    align-self: end;
  }
  label {
    margin: 0;
  }

  .spinner {
    margin-top: 24px;
  }
</style>
